import { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { symbolsClient } from "../features/symbols/api/symbolsClient";
import {
  GiAllSeeingEye,
  GiBookmarklet,
  GiCrystalBall,
  GiFeather,
  GiHouse,
  GiKey,
  GiLockedChest,
  GiMoon,
  GiMoonOrbit,
  GiOpenBook,
  GiPathDistance,
  GiQuillInk,
  GiScrollUnfurled,
  GiStarsStack,
  GiWaterDrop,
  GiWaxSeal,
} from "react-icons/gi";
import { Hero } from "./Hero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const symbolIcons = [GiMoon, GiWaterDrop, GiKey, GiHouse, GiCrystalBall, GiPathDistance];

const COPY = {
  pl: {
    dreams: {
      eyebrow: "Księga Snów",
      title: "Miejsce, do którego wracają Twoje sny",
      lead: "Morfeusz nie kończy pracy na jednym odczycie. Sny tworzą prywatną kronikę: zapisy, interpretacje, symbole, notatki i bezpieczną przestrzeń dla tego, co naprawdę osobiste.",
      cards: [
        {
          icon: GiScrollUnfurled,
          title: "Zapis i interpretacja",
          text: "Każdy sen może zostać zapisany razem z odczytem, datą, nastrojem i własną notatką.",
        },
        {
          icon: GiMoon,
          title: "Powracające symbole",
          text: "Księga pozwala zobaczyć, które obrazy wracają najczęściej i jak zmieniają się w czasie.",
        },
        {
          icon: GiAllSeeingEye,
          title: "Pytanie po analizie",
          text: "Po odczycie możesz zadać jedno dalsze pytanie, aby doprecyzować znaczenie snu bez rozbijania go na przypadkowe odpowiedzi.",
        },
      ],
      previewEyebrow: "Podgląd Księgi",
      previewTitle: "Dom nad wodą",
      previewMeta: "Ostatni sen · zapis roboczy",
      previewBody: "Stałem przed domem, którego drzwi prowadziły prosto do ciemnej, spokojnej wody. W środku paliło się jedno światło.",
      previewRead: "Odczyt wstępny",
      previewMeaning: "Sen wskazuje na próg między bezpiecznym miejscem a głęboką emocją, która domaga się spokojnego rozpoznania.",
      previewSymbols: "Symbole",
      mood: "Nastrój: cichy niepokój",
      vault: "Sejf: zamknięty",
      noteEyebrow: "Księga Snów należy do konta Morfeusza",
      note: "Księga Snów jest prywatną przestrzenią Morfeusza — miejscem zapisu, odczytu i powrotu do tego, co noc zostawiła pod powierzchnią.",
    },
    vault: {
      eyebrow: "Sejf",
      title: "Dodatkowy klucz dla wszystkich osobistych snów",
      subtitle: "Sejf chroni Księgę Snów. Jest integralną warstwą dla wpisów i notatek, które użytkownik chce zamknąć mocno — nikt tu nie zagląda.",
      items: [
        "Nie blokuje zakładania konta",
        "Chroni prywatne sny i notatki",
        "Otwiera się jednym spójnym rytmem sesji",
        "Zostaje częścią Pokoiku Morfeusza",
      ],
      status: "Sejf jest Pokoikiem, Pokoik Sejfem — prywatna Księga otwiera się tylko wtedy, gdy Morfeusz rozpoznaje właściciela.",
      label: "prywatna warstwa Księgi Snów",
    },
    trace: {
      eyebrow: "Ślad snów",
      title: "To, co powraca pod powierzchnią",
      subtitle: "Nie każdy sen jest osobną wyspą. Morfeusz może budować zaszyfrowany Ślad snów: wyciąg z powracających motywów, emocji i symboli, bez zamieniania pełnej treści snu w profil.",
      items: [
        "Powracające motywy",
        "Emocje widoczne w czasie",
        "Symbole, które wracają między snami",
        "Osobna zgoda na budowanie Śladu",
        "Osobna zgoda na użycie Śladu w analizie AI",
      ],
      note: "To użytkownik decyduje, czy Morfeusz ma budować Ślad snów i czy wolno użyć go jako kontekstu do kolejnych odczytów.",
    },
    process: {
      eyebrow: "Proces",
      title: "Jak sen trafia do Morfeusza",
      steps: [
        {
          icon: GiQuillInk,
          title: "Opisz sen",
          desc: "Zapisz obraz, nastrój, osoby i szczegóły, które zostały po przebudzeniu.",
        },
        {
          icon: GiAllSeeingEye,
          title: "Wybierz odczyt",
          desc: "Skorzystaj z analizy AI, Jung lub Freud. Wybierz tryb Szybki albo Głęboki.",
        },
        {
          icon: GiBookmarklet,
          title: "Zadaj jedno pytanie",
          desc: "Po analizie możesz doprecyzować sens snu jednym pytaniem, bez rozbijania odczytu na przypadkowy dialog.",
        },
        {
          icon: GiMoon,
          title: "Wracaj do Śladu",
          desc: "Z czasem Morfeusz pokazuje powracające motywy, emocje i symbole — jeśli zgodzisz się budować Ślad snów.",
        },
      ],
    },
    symbols: {
      eyebrow: "Atlas Symboli",
      title: "Znaki, które wracają w snach",
      subtitle: "Symbolika Morfeusza nie jest martwą listą haseł. Gdy symbol nie ma jeszcze znaczenia w bibliotece, Morfeusz odczytuje go z kontekstu i dopisuje do rosnącego atlasu nocnych obrazów.",
      catcherTitle: "Odczytaj jeden symbol ze snu",
      catcherSubtitle: "Gościnnie: 1 prosty symbol dziennie. W Księdze Snów odczytujesz symbole z kontekstem.",
      placeholder: "np. woda, dom, klucz...",
      submit: "Odczytaj symbol",
      loading: "Odczytuję…",
      empty: "Podaj symbol.",
      error: "Nie udało się odczytać symbolu.",
      fullPage: "Pełna Symbolika",
      items: [
        { name: "Księżyc", meaning: "Rytm, ukryte światło i sprawy, które dojrzewają poza spojrzeniem dnia." },
        { name: "Woda", meaning: "Emocje, pamięć ciała, przejście przez to, czego nie da się objąć prostym zdaniem." },
        { name: "Klucz", meaning: "Dostęp, granica i pytanie, czy naprawdę chcesz otworzyć to, co zostało zamknięte." },
        { name: "Dom", meaning: "Wewnętrzne miejsce, porządek psychiki, bezpieczne ściany albo pokój, do którego jeszcze nie wszedłeś." },
        { name: "Próg", meaning: "Moment przejścia: między starym a nowym, snem a jawą, lękiem a rozpoznaniem." },
        { name: "Światło", meaning: "Uwaga, przebłysk sensu i punkt, który prowadzi przez ciemniejszą część snu." },
      ],
    },
    room: {
      eyebrow: "Prywatny Pokoik",
      title: "Twoje miejsce po przebudzeniu",
      subtitle: "Po zalogowaniu Morfeusz prowadzi do Pokoiku — spokojnego miejsca, z którego wracasz do snów, dodajesz nowe wpisy, sprawdzasz Ślad snów i stan sejfu.",
      benefits: [
        "Szybki zapis snu, zanim obraz odpłynie",
        "Dodawanie i edycja snów w Księdze",
        "Analizy AI, Jung i Freud",
        "Symbolika i Ślad snów",
        "Profil, avatar i ustawienia prywatności",
        "Instalacja Morfeusza jako aplikacji",
      ],
      cta: "Wejdź do Pokoiku",
      ctaGuest: "Załóż konto",
      hint: "Konto jest bezpłatne. Pakiety i płatności będą porządkowane osobno, gdy przyjdzie czas na sklep Morfeusza.",
    },
    faq: {
      eyebrow: "Pytania",
      title: "Zanim wejdziesz do Morfeusza",
      items: [
        { q: "Czy darmowy odczyt zapisuje mój sen?", a: "Nie. Darmowy odczyt na stronie głównej jest jednorazowy. Jeśli chcesz zachowywać sny i interpretacje, załóż konto." },
        { q: "Czy moje sny są publiczne?", a: "Nie. Sny zapisane na koncie są prywatne i widoczne tylko dla Ciebie. Morfeusz nie publikuje ich automatycznie i nie pokazuje innym użytkownikom." },
        { q: "Jak działa analiza snów?", a: "Analiza uruchamia się tylko wtedy, gdy sam ją wybierzesz. Możesz wybrać odczyt AI, Jung lub Freud oraz tryb Szybki albo Głęboki." },
        { q: "Czym jest Sejf?", a: "Sejf chroni prywatną Księgę Snów. Treść zamkniętych snów można odczytać dopiero po odblokowaniu sesji użytkownika." },
        { q: "Co jeśli zapomnę hasła?", a: "Jeśli nie znasz hasła Morfeusza, prywatnych zaszyfrowanych snów nie da się odzyskać. To konsekwencja prywatności, nie błąd systemu." },
        { q: "Dlaczego sesja może się zamknąć?", a: "Dla bezpieczeństwa. Sesja może wygasnąć albo zostać zamknięta, gdy konto zostanie otwarte na innym urządzeniu." },
        { q: "Czym jest Ślad snów?", a: "To zaszyfrowany wyciąg z motywów, emocji i symboli. Użytkownik osobno decyduje, czy go budować i czy używać go w kolejnych odczytach." },
        { q: "Czy interpretacja zastępuje diagnozę albo terapię?", a: "Nie. Morfeusz jest narzędziem refleksji nad snem, symbolami i własną historią. Nie zastępuje lekarza, psychologa ani psychoterapii." },
      ],
    },
  },
  en: {
    dreams: {
      eyebrow: "Dream Codex",
      title: "A place your dreams return to",
      lead: "Morpheus does not end with one reading. Dreams form a private chronicle: entries, readings, symbols, notes and a safe space for what is truly personal.",
      cards: [
        { icon: GiScrollUnfurled, title: "Entry and reading", text: "Each dream can be saved with a reading, date, mood and your own note." },
        { icon: GiMoon, title: "Recurring symbols", text: "The Codex lets you see which images return most often and how they change over time." },
        { icon: GiAllSeeingEye, title: "Question after reading", text: "After the reading, ask one further question to clarify the dream without breaking it into random answers." },
      ],
      previewEyebrow: "Codex preview",
      previewTitle: "House by the water",
      previewMeta: "Latest dream · draft entry",
      previewBody: "I stood before a house whose door led straight into dark, quiet water. One light was burning inside.",
      previewRead: "First reading",
      previewMeaning: "The dream points to a threshold between a safe place and a deep emotion asking for calm recognition.",
      previewSymbols: "Symbols",
      mood: "Mood: quiet unease",
      vault: "Vault: closed",
      noteEyebrow: "The Dream Codex belongs to the Morpheus account",
      note: "The Dream Codex is a private Morpheus space — a place of entry, reading and returning to what the night left beneath the surface.",
    },
    vault: {
      eyebrow: "Vault",
      title: "An extra key for personal dreams",
      subtitle: "The Vault protects the Dream Codex. It is an integral layer for entries and notes that should be closed firmly — no one looks inside.",
      items: ["Does not block account creation", "Protects private dreams and notes", "Opens through one consistent session rhythm", "Remains part of the Morpheus Room"],
      status: "The Vault is the Room, the Room is the Vault — the private Codex opens only when Morpheus recognises its owner.",
      label: "private layer of the Dream Codex",
    },
    trace: {
      eyebrow: "Dream trace",
      title: "What returns beneath the surface",
      subtitle: "Not every dream is an island. Morpheus can build an encrypted Dream Trace: an extract of recurring motifs, emotions and symbols without turning the full dream text into a profile.",
      items: ["Recurring motifs", "Emotions visible over time", "Symbols returning between dreams", "Separate consent to build the Trace", "Separate consent to use the Trace in AI readings"],
      note: "You decide whether Morpheus may build the Dream Trace and whether it may be used as context for later readings.",
    },
    process: {
      eyebrow: "Process",
      title: "How a dream enters Morpheus",
      steps: [
        { icon: GiQuillInk, title: "Describe the dream", desc: "Write down the image, mood, people and details left after waking." },
        { icon: GiAllSeeingEye, title: "Choose a reading", desc: "Use AI, Jung or Freud. Choose Quick or Deep depending on how far you want to go." },
        { icon: GiBookmarklet, title: "Ask one question", desc: "After analysis, clarify the dream with one question without turning the reading into a random dialogue." },
        { icon: GiMoon, title: "Return to the Trace", desc: "Over time, Morpheus shows recurring motifs, emotions and symbols — if you agree to build the Dream Trace." },
      ],
    },
    symbols: {
      eyebrow: "Symbol Atlas",
      title: "Signs that return in dreams",
      subtitle: "Morpheus symbolism is not a dead list of terms. When a symbol is not yet in the library, Morpheus reads it from context and adds it to a growing atlas of night images.",
      catcherTitle: "Read one dream symbol",
      catcherSubtitle: "Guest: 1 simple symbol daily. In the Dream Codex you read symbols with context.",
      placeholder: "e.g. water, house, key...",
      submit: "Read symbol",
      loading: "Reading…",
      empty: "Enter a symbol.",
      error: "Could not read this symbol.",
      fullPage: "Full Symbolism",
      items: [
        { name: "Moon", meaning: "Rhythm, hidden light and matters that ripen outside the daylight gaze." },
        { name: "Water", meaning: "Emotion, body memory and passage through what cannot be held in a simple sentence." },
        { name: "Key", meaning: "Access, boundary and the question of whether you truly want to open what was closed." },
        { name: "House", meaning: "An inner place, psychic order, safe walls or a room you have not yet entered." },
        { name: "Threshold", meaning: "The moment of crossing: between old and new, dream and waking, fear and recognition." },
        { name: "Light", meaning: "Attention, a flash of meaning and the point that leads through the darker part of the dream." },
      ],
    },
    room: {
      eyebrow: "Private Room",
      title: "Your place after waking",
      subtitle: "After signing in, Morpheus leads you to the Room — a quiet place for returning to dreams, adding new entries, checking the Dream Trace and the Vault state.",
      benefits: ["Quick dream note before the image fades", "Adding and editing dreams", "AI, Jung and Freud readings", "Symbolism and Dream Trace", "Profile, avatar and privacy settings", "Install Morpheus as an app"],
      cta: "Enter the Room",
      ctaGuest: "Create account",
      hint: "The account is free. Packages and payments will be organised separately when the Morpheus shop is ready.",
    },
    faq: {
      eyebrow: "Questions",
      title: "Before you enter Morpheus",
      items: [
        { q: "Does the free reading save my dream?", a: "No. The free reading on the home page is one-time. Create an account if you want to keep dreams and interpretations." },
        { q: "Are my dreams public?", a: "No. Dreams saved on your account are private and visible only to you. Morpheus does not publish them automatically or show them to others." },
        { q: "How does dream analysis work?", a: "Analysis starts only when you choose it. You can choose AI, Jung or Freud, and Quick or Deep mode." },
        { q: "What is the Vault?", a: "The Vault protects the private Dream Codex. Closed dream content can be read only after unlocking the user's session." },
        { q: "What if I forget my password?", a: "If you do not know your Morpheus password, private encrypted dreams cannot be recovered. This is the consequence of privacy, not a system error." },
        { q: "Why can a session close?", a: "For safety. A session may expire or be closed when the account is opened on another device." },
        { q: "What is the Dream Trace?", a: "It is an encrypted extract of motifs, emotions and symbols. You decide separately whether to build it and whether to use it in later readings." },
        { q: "Does interpretation replace diagnosis or therapy?", a: "No. Morpheus is a reflection tool for dreams, symbols and your own history. It does not replace medical, psychological or psychotherapeutic help." },
      ],
    },
  },
};

const SectionTitle = ({ eyebrow, title, subtitle }) => (
  <div className="md:grid md:grid-cols-12 md:gap-12 items-end" data-ui="home-v2-heading">
    <div className="md:col-span-7">
      <span className="font-accent text-[11px] uppercase tracking-[0.3em] text-gold-muted">{eyebrow}</span>
      <h2 className="font-heading italic text-4xl md:text-5xl text-ink mt-3 leading-tight">{title}</h2>
    </div>
    {subtitle && <p className="md:col-span-5 font-body text-base md:text-lg text-ink-soft leading-relaxed mt-5 md:mt-0">{subtitle}</p>}
  </div>
);

const DreamsCodexSection = ({ copy }) => (
  <section id="dreams" className="parchment-texture py-24 md:py-32 border-y border-ink/10" data-ui="home-v2-dreams">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <SectionTitle eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.lead} />

      <div className="mt-14 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10" data-ui="home-v2-dreams-cards">
        {copy.cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className="bg-parchment p-8 md:p-10 group hover:bg-parchment-dark transition-colors" data-ui="home-v2-dream-card">
              <div className="flex items-start justify-between gap-4">
                <div className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted">{String(index + 1).padStart(2, "0")}</div>
                <Icon className="text-3xl text-ink-soft/60 group-hover:text-gold-muted transition-colors" />
              </div>
              <h3 className="font-heading italic text-3xl text-ink mt-12">{card.title}</h3>
              <p className="font-body text-sm text-ink-soft mt-4 leading-relaxed">{card.text}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-16 ornate-frame bg-parchment p-1" data-ui="home-v2-codex-preview">
        <div className="grid lg:grid-cols-12 gap-px bg-ink/10">
          <div className="lg:col-span-7 bg-parchment p-8 md:p-10">
            <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted">{copy.previewEyebrow}</p>
            <h3 className="font-heading italic text-4xl text-ink mt-5">{copy.previewTitle}</h3>
            <p className="font-body italic text-sm text-ink-soft mt-2">{copy.previewMeta}</p>
            <p className="font-body text-base text-ink mt-8 leading-relaxed">{copy.previewBody}</p>
            <div className="mt-8 pt-6 border-t border-ink/10">
              <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-gold-muted">{copy.previewRead}</p>
              <p className="font-heading italic text-xl text-ink mt-4 leading-relaxed">{copy.previewMeaning}</p>
            </div>
          </div>
          <div className="lg:col-span-5 bg-parchment p-8 md:p-10 flex flex-col justify-between gap-10">
            <div>
              <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-gold-muted inline-flex items-center gap-2"><GiMoon className="text-base" />{copy.previewSymbols}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                {["Woda", "Dom", "Próg", "Światło"].map((item) => <span key={item} className="border border-gold-muted/30 px-4 py-2 font-accent text-[10px] uppercase tracking-[0.25em] text-gold-muted">{item}</span>)}
              </div>
            </div>
            <div className="space-y-4 font-body text-sm text-ink-soft">
              <p className="inline-flex items-center gap-3"><GiWaterDrop className="text-gold-muted" />{copy.mood}</p>
              <p className="inline-flex items-center gap-3"><GiLockedChest className="text-gold-muted" />{copy.vault}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 border border-gold-muted/35 p-6 md:p-8" data-ui="home-v2-dream-note">
        <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-gold-muted inline-flex items-center gap-3"><GiOpenBook className="text-xl" />{copy.noteEyebrow}</p>
        <p className="font-heading italic text-xl text-ink mt-4 leading-relaxed">{copy.note}</p>
      </div>
    </div>
  </section>
);

const VaultSectionV2 = ({ copy }) => (
  <section id="vault" className="bg-parchment-dark py-24 md:py-32 border-y border-ink/10" data-ui="home-v2-vault">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5 ornate-frame bg-parchment-dark p-1" data-ui="home-v2-vault-card">
          <div className="bg-parchment-dark p-10 md:p-12 text-center">
            <div className="flex justify-center text-gold-muted"><GiLockedChest className="text-6xl" /></div>
            <div className="divider-ornate mt-8 opacity-70">●</div>
            <div className="mt-8 grid gap-4 text-left">
              {copy.items.map((item) => <div key={item} className="flex items-start gap-3 font-body text-sm text-ink-soft leading-relaxed" data-ui="home-v2-vault-item"><GiWaxSeal className="text-gold-muted text-lg flex-shrink-0 mt-0.5" /><span>{item}</span></div>)}
            </div>
          </div>
        </div>
        <div className="lg:col-span-7" data-ui="home-v2-vault-copy">
          <span className="font-accent text-[11px] uppercase tracking-[0.3em] text-gold-muted">{copy.eyebrow}</span>
          <h2 className="font-heading italic text-4xl md:text-5xl text-ink mt-4 leading-tight">{copy.title}</h2>
          <p className="font-body text-base md:text-lg text-ink-soft mt-6 leading-relaxed max-w-2xl">{copy.subtitle}</p>
          <div className="mt-8 flex items-start gap-4 border border-gold-muted/40 bg-parchment p-6">
            <GiKey className="text-2xl text-gold-muted flex-shrink-0 mt-1" />
            <p className="font-body italic text-sm text-ink-soft leading-relaxed">{copy.status}</p>
          </div>
          <div className="mt-8 flex items-center gap-3 font-accent text-[10px] uppercase tracking-[0.25em] text-ink-soft"><GiLockedChest className="text-gold-muted text-lg" />{copy.label}</div>
        </div>
      </div>
    </div>
  </section>
);

const TraceSection = ({ copy }) => (
  <section id="trace" className="parchment-texture py-24 md:py-32" data-ui="home-v2-trace">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <SectionTitle eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />
      <div className="mt-14 grid md:grid-cols-5 gap-4" data-ui="home-v2-trace-items">
        {copy.items.map((item, index) => <div key={item} className="border border-ink/10 bg-parchment/80 p-6" data-ui="home-v2-trace-item"><div className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted">{String(index + 1).padStart(2, "0")}</div><p className="font-body text-sm text-ink-soft mt-5 leading-relaxed">{item}</p></div>)}
      </div>
      <div className="mt-10 border border-gold-muted/35 bg-parchment-dark/60 p-6 md:p-8 font-heading italic text-xl text-ink leading-relaxed" data-ui="home-v2-trace-note">{copy.note}</div>
    </div>
  </section>
);

const ProcessSection = ({ copy }) => (
  <section id="how" className="bg-parchment-dark py-24 md:py-32 border-y border-ink/10" data-ui="home-v2-process">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="text-center max-w-2xl mx-auto"><span className="font-accent text-[11px] uppercase tracking-[0.3em] text-gold-muted">{copy.eyebrow}</span><h2 className="font-heading italic text-4xl md:text-5xl text-ink mt-4">{copy.title}</h2><div className="divider-ornate mt-8 max-w-xs mx-auto">●</div></div>
      <div className="mt-16 grid md:grid-cols-4 gap-px bg-ink/10">
        {copy.steps.map((step, index) => { const Icon = step.icon; return <div key={step.title} className="bg-parchment-dark p-8 md:p-10 text-center group hover:bg-parchment transition-colors" data-ui="home-v2-process-step"><div className="flex justify-center"><div className="w-16 h-16 rounded-full border border-gold-muted/60 flex items-center justify-center group-hover:border-gold-muted transition-colors"><Icon className="text-2xl text-ink group-hover:text-gold-muted transition-colors" /></div></div><div className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted mt-6">{String(index + 1).padStart(2, "0")}</div><h3 className="font-heading italic text-2xl md:text-3xl text-ink mt-3">{step.title}</h3><p className="font-body text-sm text-ink-soft mt-4 leading-relaxed">{step.desc}</p></div>; })}
      </div>
    </div>
  </section>
);

const SymbolsSectionV2 = ({ copy, lang }) => {
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const symbol = term.trim();
    if (!symbol) { setStatus(copy.empty); return; }
    setLoading(true); setStatus(""); setResult(null);
    try {
      const response = await symbolsClient.interpret({ terms: [symbol], context: "", language: lang });
      setResult(response?.results?.[0] || null);
    } catch (error) {
      setStatus(error?.message || copy.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="symbols" className="parchment-texture py-24 md:py-32" data-ui="home-v2-symbols">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <SectionTitle eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />
        <form className="mdCard mt-14" data-ui="home-v2-symbol-catcher" onSubmit={handleSubmit} noValidate>
          <div className="text-center max-w-3xl mx-auto"><p className="mdPage__eyebrow">{copy.catcherTitle}</p><p className="mdPage__subtitle">{copy.catcherSubtitle}</p></div>
          <div className="mdField mt-8"><input className="mdField__input" type="text" value={term} onChange={(event) => setTerm(event.target.value)} placeholder={copy.placeholder} data-ui="home-v2-symbol-input" /></div>
          <div className="mdRow" data-ui="home-v2-symbol-actions"><button type="submit" className="mdBtn mdBtn--primary" disabled={loading || !term.trim()}>{loading ? copy.loading : copy.submit}</button><a className="mdBtn" href="#/symbolika">{copy.fullPage}</a></div>
          {status && <div className="mdAlert" data-ui="home-v2-symbol-status">{status}</div>}
          {result && <div className="mdFinder__result" data-ui="home-v2-symbol-result"><h3 className="mdFinder__resultName">{result.term}</h3><p className="mdFinder__resultMeaning">{result.meaning}</p></div>}
        </form>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-ui="home-v2-symbol-grid">
          {copy.items.map((symbol, index) => { const Icon = symbolIcons[index % symbolIcons.length]; return <div key={symbol.name} className="border border-ink/10 bg-parchment p-8 md:p-10 group hover:bg-parchment-dark transition-colors duration-500" data-ui="home-v2-symbol-card"><div className="flex items-start justify-between"><div className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted">{String(index + 1).padStart(2, "0")}</div><Icon className="text-3xl text-ink-soft/60 group-hover:text-gold-muted transition-colors" /></div><h3 className="font-heading italic text-3xl text-ink mt-10">{symbol.name}</h3><p className="font-body text-sm text-ink-soft mt-4 leading-relaxed">{symbol.meaning}</p></div>; })}
        </div>
      </div>
    </section>
  );
};

const RoomSection = ({ copy }) => (
  <section id="pricing" className="bg-ink-deep py-24 md:py-32 relative overflow-hidden" data-ui="home-v2-room">
    <div className="absolute inset-0 opacity-[0.07] pointer-events-none home-pricing-aura" />
    <div className="max-w-4xl mx-auto px-6 md:px-10 relative">
      <div className="border border-gold-muted/40 p-1 home-pricing-frame">
        <div className="border border-gold-muted/30 p-10 md:p-16 text-center">
          <div className="flex justify-center text-gold-muted"><GiMoonOrbit className="text-4xl" /></div>
          <span className="font-accent text-[11px] uppercase tracking-[0.35em] text-gold-muted mt-6 block">{copy.eyebrow}</span>
          <h2 className="font-heading italic text-4xl md:text-5xl text-parchment mt-4 leading-tight">{copy.title}</h2>
          <p className="font-body text-base md:text-lg text-parchment/70 mt-6 max-w-2xl mx-auto leading-relaxed">{copy.subtitle}</p>
          <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4 text-left max-w-2xl mx-auto">
            {copy.benefits.map((benefit, index) => <li key={benefit} className="flex items-start gap-3 font-body text-parchment/85"><span className="text-gold-muted font-heading text-xl leading-none mt-1">{index === 0 ? <GiFeather className="text-base" /> : <GiStarsStack className="text-base" />}</span><span>{benefit}</span></li>)}
          </ul>
          <div className="mt-12"><a href="#/rejestracja" className="inline-flex items-center gap-3 bg-gold text-ink-deep px-9 py-4 font-accent text-[12px] uppercase tracking-[0.25em] hover:bg-gold-light transition-colors"><GiOpenBook className="text-base" />{copy.ctaGuest}</a><div className="font-body italic text-sm text-parchment/50 mt-4">{copy.hint}</div></div>
        </div>
      </div>
    </div>
  </section>
);

const FaqSectionV2 = ({ copy }) => (
  <section id="faq" className="parchment-texture py-24 md:py-32" data-ui="home-v2-faq">
    <div className="max-w-3xl mx-auto px-6 md:px-10">
      <div className="text-center mb-12"><span className="font-accent text-[11px] uppercase tracking-[0.3em] text-gold-muted">{copy.eyebrow}</span><h2 className="font-heading italic text-4xl md:text-5xl text-ink mt-3">{copy.title}</h2></div>
      <Accordion type="single" collapsible className="w-full">
        {copy.items.map((item, index) => <AccordionItem key={item.q} value={`item-${index}`} className="border-b border-ink/15" data-ui="home-v2-faq-item"><AccordionTrigger className="font-heading text-xl md:text-2xl text-ink hover:text-gold-muted text-left py-6 hover:no-underline">{item.q}</AccordionTrigger><AccordionContent className="font-body text-base text-ink-soft leading-relaxed pb-6">{item.a}</AccordionContent></AccordionItem>)}
      </Accordion>
    </div>
  </section>
);

export const HomePageV2 = () => {
  const { lang } = useLang();
  const copy = COPY[lang] ?? COPY.pl;
  return (
    <>
      <Hero />
      <DreamsCodexSection copy={copy.dreams} />
      <VaultSectionV2 copy={copy.vault} />
      <TraceSection copy={copy.trace} />
      <ProcessSection copy={copy.process} />
      <SymbolsSectionV2 copy={copy.symbols} lang={lang} />
      <RoomSection copy={copy.room} />
      <FaqSectionV2 copy={copy.faq} />
    </>
  );
};
